<template>
  <div id="app">
    <header class="app-header">
      <div>
        <p class="eyebrow">Deployment planning workspace</p>
        <h1>Customer Manager</h1>
      </div>
      <div class="global-actions">
        <button type="button" class="ghost" @click="generate(5)">
          Generate 5
        </button>
        <button type="button" class="ghost" @click="generate(10)">
          Generate 10
        </button>
        <button type="button" class="danger" @click="removeAll">
          Delete All Data
        </button>
      </div>
    </header>

    <p
      v-if="state.ui.notice"
      :class="['notice', `notice--${state.ui.notice.kind}`]"
    >
      <span>{{ state.ui.notice.text }}</span>
      <button type="button" @click="clearNotice">Dismiss</button>
    </p>

    <nav class="tabs">
      <router-link
        v-for="tab in tabs"
        :key="tab.path"
        :to="tab.path"
        class="tab"
      >
        {{ tab.label }}
      </router-link>
    </nav>

    <main class="workspace">
      <router-view />
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useRouter } from "vue-router";
import { useCustomerManager } from "@/store/customerManager";

const tabs = [
  { path: "/", label: "Home" },
  { path: "/users", label: "Users" },
  { path: "/deploy-units", label: "Deploy Units" },
  { path: "/deploy-steps", label: "Deploy Steps" },
  { path: "/rings", label: "Rings" },
  { path: "/customers", label: "Customers" },
  { path: "/notes", label: "Notes" },
  { path: "/planned", label: "Planned" },
  { path: "/deploy", label: "Deploy" },
  { path: "/history", label: "History" },
  { path: "/branches", label: "Branches" },
  { path: "/applications", label: "Applications" },
  { path: "/docs", label: "Docs" },
];

export default defineComponent({
  name: "App",
  setup() {
    const router = useRouter();
    const manager = useCustomerManager();

    const generate = (size: number) => {
      manager.generateDemoData(size);
      router.push("/");
    };

    const removeAll = () => {
      if (!window.confirm("Delete all persisted business data?")) {
        return;
      }
      manager.deleteAllData();
      router.push("/");
    };

    return {
      tabs,
      state: manager.state,
      clearNotice: manager.clearNotice,
      generate,
      removeAll,
    };
  },
});
</script>

<style lang="scss">
:root {
  color-scheme: light;
  font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #18212f;
  background: #f4f7fb;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: #f4f7fb;
}

button,
input,
select,
textarea {
  font: inherit;
}

button {
  cursor: pointer;
}

#app {
  min-height: 100vh;
  padding: 24px;
}

.app-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.eyebrow {
  margin: 0 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  color: #56708f;
}

h1 {
  margin: 0;
  font-size: 2rem;
}

.global-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

button {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  background: #1d4ed8;
  color: #fff;
}

button.ghost {
  background: #dbeafe;
  color: #1d4ed8;
}

button.danger {
  background: #b91c1c;
}

.notice {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  border-radius: 12px;
  padding: 12px 16px;
  margin: 0 0 16px;
}

.notice button {
  background: transparent;
  color: inherit;
  padding: 0;
}

.notice--success {
  background: #dcfce7;
  color: #166534;
}

.notice--error {
  background: #fee2e2;
  color: #991b1b;
}

.notice--info {
  background: #dbeafe;
  color: #1d4ed8;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.tab {
  padding: 10px 14px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  text-decoration: none;
  font-weight: 600;
}

.tab.router-link-exact-active {
  background: #1d4ed8;
  color: #fff;
}

.workspace {
  display: block;
}

@media (max-width: 960px) {
  #app {
    padding: 16px;
  }

  .app-header {
    flex-direction: column;
  }
}
</style>
